import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar se há token salvo ao carregar
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, []);

  // Login
  const login = async (email, senha) => {
    try {
      setError(null);
      const response = await api.post('/api/auth/login', { email, senha });
      
      const { token, operador } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(operador));
      
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(operador);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Erro ao fazer login';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Registro
  const register = async (nome, email, senha) => {
    try {
      setError(null);
      const response = await api.post('/api/auth/register', { nome, email, senha });

      if (response.data?.pendente) {
        return { success: true, pendente: true, message: response.data.message };
      }

      const { token, operador } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(operador));
      
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(operador);
      return { success: true };
    } catch (err) {
      const data = err.response?.data || {};
      const message = [data.error || 'Erro ao criar conta', data.hint].filter(Boolean).join(' — ');
      setError(message);
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Atualizar senha
  const updatePassword = async (senhaAtual, novaSenha) => {
    try {
      setError(null);
      await api.put('/api/auth/password', { senhaAtual, novaSenha });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Erro ao atualizar senha';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Verificar se está autenticado
  const isAuthenticated = !!user;

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    updatePassword,
    isAuthenticated,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
